import type { QueryClient } from "@tanstack/react-query";

export const BILLING_PORTAL_ERROR = "We couldn’t open billing. Please try again or contact support.";
export const BILLING_PORTAL_TIMEOUT_MS = 15_000;

export async function requestSettingsBillingPortal(
  csrfHeaders: Record<string, string>,
  { fetcher = fetch, timeoutMs = BILLING_PORTAL_TIMEOUT_MS }:
    { fetcher?: typeof fetch; timeoutMs?: number } = {},
): Promise<string> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetcher("/api/stripe/create-portal-session", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...csrfHeaders },
          signal: controller.signal,
        });
        const body = await response.json();
        if (body.impersonationReadOnly) {
          throw Object.assign(new Error(body.message), { impersonationReadOnly: true });
        }
        if (!response.ok || typeof body.url !== "string" || !body.url.trim()) {
          throw new Error(BILLING_PORTAL_ERROR);
        }
        return body.url;
      })(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error(BILLING_PORTAL_ERROR));
          controller.abort();
        }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export async function refreshSettingsBilling(
  client: Pick<QueryClient, "invalidateQueries">,
  cancelRefetch = true,
) {
  await Promise.all([
    client.invalidateQueries({ queryKey: ["/api/auth/user"], exact: true }, { cancelRefetch }),
    client.invalidateQueries({ queryKey: ["/api/stripe/subscription-status"], exact: true }, { cancelRefetch }),
  ]);
}

export function watchSettingsBillingReturn(refresh: () => void, win = window, doc = document) {
  const onVisible = () => { if (doc.visibilityState === "visible") refresh(); };
  win.addEventListener("focus", refresh);
  win.addEventListener("pageshow", refresh);
  doc.addEventListener("visibilitychange", onVisible);
  return () => {
    win.removeEventListener("focus", refresh);
    win.removeEventListener("pageshow", refresh);
    doc.removeEventListener("visibilitychange", onVisible);
  };
}
